import axios from 'axios';
import { z } from 'zod';
import type { ProductWarehouseNote } from '../schemas/apiResponse.schemas';
import { ProductWarehouseNoteSchema } from '../schemas/apiResponse.schemas';
import { dedupeInFlight, skuListCacheKey } from '../utils/inFlightByKey';
import { validateResponse } from './validateResponse';

const API_BASE = '/api/supabase/product-warehouse-notes';

const SkusWithNotesMapSchema = z.record(z.string(), z.literal(true));

export const fetchSkusWithNotes = async (skus: string[]): Promise<Record<string, true>> => {
  const list = Array.isArray(skus) ? skus : [];
  if (list.length === 0) {
    return {};
  }
  const key = `notes-by-skus:${skuListCacheKey(list)}`;
  return dedupeInFlight(key, async () => {
    try {
      const response = await axios.post(`${API_BASE}/by-skus`, { skus: list });
      return validateResponse(SkusWithNotesMapSchema, response.data || {}, 'fetchSkusWithNotes');
    } catch (error: unknown) {
      console.error('Error fetching SKUs with warehouse notes:', error);
      return {};
    }
  });
};

export const getNoteBySku = async (sku: string): Promise<ProductWarehouseNote | null> => {
  try {
    const trimmed = String(sku || '').trim();
    if (!trimmed) return null;
    const response = await axios.get(`${API_BASE}/${encodeURIComponent(trimmed)}`);
    if (response.data == null) return null;
    return validateResponse(ProductWarehouseNoteSchema, response.data, 'getNoteBySku');
  } catch (error: unknown) {
    console.error('Error fetching product warehouse note:', error);
    throw error;
  }
};

export const createNote = async (sku: string, note: string): Promise<ProductWarehouseNote> => {
  const response = await axios.post(API_BASE, { sku: String(sku).trim(), note: String(note) });
  return validateResponse(ProductWarehouseNoteSchema, response.data, 'createNote');
};

export const updateNote = async (id: number, note: string): Promise<ProductWarehouseNote> => {
  const response = await axios.put(`${API_BASE}/${id}`, { note: String(note) });
  return validateResponse(ProductWarehouseNoteSchema, response.data, 'updateNote');
};

export const deleteNote = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/${id}`);
};
